import React, { useMemo, useState } from "react";
import fontkit from "@pdf-lib/fontkit";
import { PDFDocument, rgb } from "pdf-lib";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";
import "./OzonFbsLabelSorter.css";
import { extractLabelPageOrderId } from "./ozonFbsLabelParsing";

const ORDER_ID_REGEX = /(\d{8,10}-\d{4}-\d{1,4})/g;
const ARTICLE_TOKEN_REGEX = /^[A-Za-z0-9_./-]+$/;

// Must be set to a non-empty string to satisfy pdfjs validation,
// but the actual file won't be fetched — globalThis.pdfjsWorker is set below.
GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

// Webpack bundles the worker into a separate chunk. When that chunk loads it sets
// globalThis.pdfjsWorker = { WorkerMessageHandler }, which pdfjs detects and uses
// its built-in LoopbackPort to run the worker in the main thread — no fetch needed.
let _workerPromise = null;
function ensureWorkerLoaded() {
  if (!_workerPromise) {
    _workerPromise = import("pdfjs-dist/legacy/build/pdf.worker.min.mjs");
  }
  return _workerPromise;
}

const NUNITO_REGULAR_URL = `${process.env.PUBLIC_URL || ""}/fonts/nunito/Nunito-Regular.ttf`;
const NUNITO_BOLD_URL = `${process.env.PUBLIC_URL || ""}/fonts/nunito/Nunito-Bold.ttf`;

function normalizeText(value) {
  return value
    .replace(/\u00a0/g, " ")
    .replace(/\s*\|\s*/g, " | ")
    .replace(/\s+/g, " ")
    .trim();
}

function readFileAsUint8Array(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(new Error("Не удалось прочитать файл."));
    reader.readAsArrayBuffer(file);
  });
}

async function fetchFontBytes(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Не удалось загрузить шрифт для итогового PDF.");
  }
  return new Uint8Array(await response.arrayBuffer());
}

async function extractPdfTexts(bytes) {
  await ensureWorkerLoaded();
  const loadingTask = getDocument({ data: bytes });
  const pdf = await loadingTask.promise;
  const pages = [];

  for (let index = 1; index <= pdf.numPages; index += 1) {
    const page = await pdf.getPage(index);
    const content = await page.getTextContent();
    const text = content.items
      .map((item) => ("str" in item ? item.str : ""))
      .join(" | ");
    pages.push(normalizeText(text));
  }

  return pages;
}

function isArticleCandidate(tokens) {
  if (!tokens.length) return false;
  if (!/\d/.test(tokens[0])) return false;
  if (tokens.join(" ").length > 48) return false;
  if (tokens.some((token) => !ARTICLE_TOKEN_REGEX.test(token))) return false;
  return ((tokens.join("").match(/\d/g) || []).length >= 3);
}

function splitProductAndArticle(chunks) {
  const cleanChunks = chunks.map((chunk) => chunk.trim()).filter(Boolean);
  if (!cleanChunks.length) return null;

  if (cleanChunks.length >= 2) {
    const lastChunk = cleanChunks[cleanChunks.length - 1];
    const prevChunk = cleanChunks[cleanChunks.length - 2];
    const isWrappedArticleTail = /^[A-Za-z0-9]{1,3}$/.test(lastChunk)
      && /[\dA-Za-z/_-]$/.test(prevChunk)
      && /[-_/]|\d{4,}/.test(prevChunk);

    if (isWrappedArticleTail) {
      const mergedChunks = [
        ...cleanChunks.slice(0, -2),
        `${prevChunk}${lastChunk}`,
      ];
      return splitProductAndArticle(mergedChunks);
    }
  }

  if (cleanChunks.length >= 2) {
    const article = cleanChunks[cleanChunks.length - 1];
    if (isArticleCandidate(article.split(/\s+/)) && !/^\d{1,3}$/.test(article)) {
      return {
        product: cleanChunks.slice(0, -1).join(" ").replace(/\s+/g, " ").trim(),
        article,
      };
    }
  }

  const merged = cleanChunks.join(" ").replace(/\s+/g, " ").trim();

  if (!merged) return null;

  const tokens = merged.split(" ");
  for (let size = Math.min(2, tokens.length); size >= 1; size -= 1) {
    const articleTokens = tokens.slice(-size);
    if (!isArticleCandidate(articleTokens)) continue;
    const product = tokens.slice(0, -size).join(" ").trim();
    if (!product) continue;
    return {
      product,
      article: articleTokens.join(" "),
    };
  }

  return null;
}

function parseAssemblySegment(segment) {
  const orderMatch = segment.match(/^(\d{8,10}-\d{4}-\d{1,4})\s*(.*)$/);
  if (!orderMatch) return [];

  const orderId = orderMatch[1];
  const body = orderMatch[2].trim();
  const chunks = body
    .split("|")
    .map((chunk) => chunk.trim())
    .filter(Boolean);
  const items = [];
  let cursor = 0;

  while (cursor < chunks.length) {
    let bestCandidate = null;

    for (let end = cursor + 1; end < chunks.length; end += 1) {
      const quantityChunk = chunks[end];
      if (!/^\d+$/.test(quantityChunk)) continue;

      const parsed = splitProductAndArticle(chunks.slice(cursor, end));
      if (!parsed) continue;

      const quantity = Number(quantityChunk);
      if (!Number.isFinite(quantity) || quantity <= 0) continue;

      const hasLabel = end + 1 < chunks.length && /^\d{4}$/.test(chunks[end + 1]);
      const candidate = {
        orderId,
        product: parsed.product,
        article: parsed.article,
        quantity,
        labelCode: hasLabel ? chunks[end + 1] : null,
        hasTrailingData: false,
        nextCursor: hasLabel ? end + 2 : end + 1,
      };

      if (hasLabel) {
        bestCandidate = candidate;
        break;
      }

      if (!bestCandidate) {
        bestCandidate = candidate;
      }
    }

    if (!bestCandidate) break;

    items.push({
      orderId: bestCandidate.orderId,
      product: bestCandidate.product,
      article: bestCandidate.article,
      quantity: bestCandidate.quantity,
      labelCode: bestCandidate.labelCode,
      hasTrailingData: false,
    });

    cursor = bestCandidate.nextCursor;
    while (cursor < chunks.length && /^\d+$/.test(chunks[cursor])) cursor += 1;
  }

  return items;
}

function parseAssemblyRecords(pageTexts) {
  const records = [];

  pageTexts.forEach((pageText) => {
    const matches = Array.from(pageText.matchAll(ORDER_ID_REGEX));
    for (let index = 0; index < matches.length; index += 1) {
      const match = matches[index];
      const nextStart = index + 1 < matches.length ? matches[index + 1].index : pageText.length;
      const segment = pageText.slice(match.index, nextStart).trim();
      const segmentRecords = parseAssemblySegment(segment);
      if (segmentRecords.length) records.push(...segmentRecords);
    }
  });

  return records;
}

function buildAssemblyPlan(records, labelPages) {
  const pageMap = new Map(labelPages.map((entry) => [entry.orderId, entry.pageIndex]));
  const orderItems = new Map();
  const unmatchedOrders = [];
  const seenOrders = new Set();
  const simpleGroups = new Map();
  const complexOrders = [];

  records.forEach((record) => {
    if (!orderItems.has(record.orderId)) orderItems.set(record.orderId, []);
    orderItems.get(record.orderId).push(record);
  });

  records.forEach((record) => {
    if (seenOrders.has(record.orderId)) return;
    seenOrders.add(record.orderId);

    const pageIndex = pageMap.get(record.orderId);
    if (pageIndex === undefined) {
      unmatchedOrders.push(record.orderId);
      return;
    }

    const items = orderItems.get(record.orderId) || [];
    const isComplex = items.length > 1 || items.some((item) => item.quantity > 1 || item.hasTrailingData);

    if (isComplex) {
      complexOrders.push({
        orderId: record.orderId,
        pageIndex,
        items,
      });
      return;
    }

    const key = record.article;
    if (!simpleGroups.has(key)) {
      simpleGroups.set(key, {
        article: record.article,
        product: record.product,
        orders: [],
      });
    }

    simpleGroups.get(key).orders.push({
      orderId: record.orderId,
      pageIndex,
    });
  });

  const orphanPages = labelPages
    .filter((entry) => !seenOrders.has(entry.orderId))
    .map((entry) => entry.orderId);

  return {
    simpleGroups: Array.from(simpleGroups.values()),
    complexOrders,
    unmatchedOrders,
    orphanPages,
  };
}

function formatSummary(plan) {
  const simpleLabels = plan.simpleGroups.reduce((sum, group) => sum + group.orders.length, 0);
  return {
    groupCount: plan.simpleGroups.length,
    simpleLabels,
    complexLabels: plan.complexOrders.length,
  };
}

function wrapText(text, font, size, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return [];

  const lines = [];
  let current = words[0];

  for (let index = 1; index < words.length; index += 1) {
    const candidate = `${current} ${words[index]}`;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = words[index];
    }
  }

  lines.push(current);
  return lines;
}

function drawDividerPage(pdfDoc, pageSize, fonts, payload) {
  const page = pdfDoc.addPage([pageSize.width, pageSize.height]);
  const { width, height } = page.getSize();
  const margin = Math.min(width, height) * 0.12;
  const usableWidth = width - margin * 2;

  page.drawRectangle({
    x: 0,
    y: 0,
    width,
    height,
    color: rgb(0.956, 0.965, 0.984),
  });

  page.drawRectangle({
    x: margin * 0.45,
    y: margin * 0.45,
    width: width - margin * 0.9,
    height: height - margin * 0.9,
    borderColor: rgb(0.145, 0.286, 0.556),
    borderWidth: 1.25,
    color: rgb(1, 1, 1),
  });

  const titleSize = Math.max(6, Math.min(9, width * 0.016));
  const bodySize = Math.max(5, Math.min(7, width * 0.012));
  const metaSize = Math.max(8, Math.min(10, width * 0.018));
  let cursorY = height - margin * 1.2;

  wrapText(payload.title, fonts.bold, titleSize, usableWidth).forEach((line) => {
    page.drawText(line, {
      x: margin,
      y: cursorY,
      font: fonts.bold,
      size: titleSize,
      color: rgb(0.09, 0.14, 0.25),
    });
    cursorY -= titleSize * 1.2;
  });

  if (payload.subtitle) {
    cursorY -= bodySize * 0.2;
    wrapText(payload.subtitle, fonts.regular, bodySize, usableWidth).forEach((line) => {
      page.drawText(line, {
        x: margin,
        y: cursorY,
        font: fonts.regular,
        size: bodySize,
        color: rgb(0.23, 0.29, 0.41),
      });
      cursorY -= bodySize * 1.25;
    });
  }

  if (payload.footer) {
    page.drawText(payload.footer, {
      x: margin,
      y: margin * 0.85,
      font: fonts.regular,
      size: metaSize,
      color: rgb(0.5, 0.56, 0.68),
    });
  }
}

function fitComplexListFontSize(items, pageWidth) {
  const longest = items.reduce((max, item) => {
    const value = `${item.product} ${item.article}`.length;
    return Math.max(max, value);
  }, 0);

  if (items.length >= 5 || longest > 90) return Math.max(5, Math.min(6, pageWidth * 0.01));
  if (items.length >= 3 || longest > 60) return Math.max(6, Math.min(7, pageWidth * 0.012));
  return Math.max(7, Math.min(8, pageWidth * 0.014));
}

function drawComplexOrderDividerPage(pdfDoc, pageSize, fonts, order) {
  const page = pdfDoc.addPage([pageSize.width, pageSize.height]);
  const { width, height } = page.getSize();
  const margin = Math.min(width, height) * 0.12;
  const usableWidth = width - margin * 2;

  page.drawRectangle({
    x: 0,
    y: 0,
    width,
    height,
    color: rgb(0.956, 0.965, 0.984),
  });

  page.drawRectangle({
    x: margin * 0.45,
    y: margin * 0.45,
    width: width - margin * 0.9,
    height: height - margin * 0.9,
    borderColor: rgb(0.145, 0.286, 0.556),
    borderWidth: 1.25,
    color: rgb(1, 1, 1),
  });

  const titleSize = Math.max(10, Math.min(13, width * 0.022));
  const bodySize = Math.max(6, Math.min(8, width * 0.014));
  let cursorY = height - margin * 1.2;

  page.drawText("Сборный заказ", {
    x: margin,
    y: cursorY,
    font: fonts.bold,
    size: titleSize,
    color: rgb(0.09, 0.14, 0.25),
  });

  cursorY -= titleSize * 1.4;
  page.drawText(order.orderId, {
    x: margin,
    y: cursorY,
    font: fonts.regular,
    size: bodySize,
    color: rgb(0.32, 0.4, 0.56),
  });

  cursorY -= bodySize * 1.5;
  const listFontSize = fitComplexListFontSize(order.items, width);
  const itemLines = order.items.flatMap((item, index) => {
    const base = `${index + 1}. ${item.product}`;
    const articleLine = `арт. ${item.article} × ${item.quantity}`;
    return [
      ...wrapText(base, fonts.regular, listFontSize, usableWidth),
      ...wrapText(articleLine, fonts.regular, Math.max(5, listFontSize - 1), usableWidth),
    ];
  });

  itemLines.forEach((line) => {
    if (cursorY <= margin * 1.8) return;
    const size = line.startsWith("арт. ") ? Math.max(5, listFontSize - 1) : listFontSize;
    page.drawText(line, {
      x: margin,
      y: cursorY,
      font: fonts.regular,
      size,
      color: rgb(0.18, 0.24, 0.35),
    });
    cursorY -= size * 1.22;
  });
}

async function buildOutputPdf(labelBytes, plan) {
  const sourcePdf = await PDFDocument.load(labelBytes);
  const outputPdf = await PDFDocument.create();
  outputPdf.registerFontkit(fontkit);
  const [regularFontBytes, boldFontBytes] = await Promise.all([
    fetchFontBytes(NUNITO_REGULAR_URL),
    fetchFontBytes(NUNITO_BOLD_URL),
  ]);
  const fonts = {
    regular: await outputPdf.embedFont(regularFontBytes),
    bold: await outputPdf.embedFont(boldFontBytes),
  };
  const firstPage = sourcePdf.getPage(0);
  const pageSize = {
    width: firstPage.getWidth(),
    height: firstPage.getHeight(),
  };

  for (const group of plan.simpleGroups) {
    drawDividerPage(outputPdf, pageSize, fonts, {
      title: group.product,
      subtitle: `Артикул: ${group.article}`,
      footer: `Этикеток в группе: ${group.orders.length}`,
    });

    const copiedPages = await outputPdf.copyPages(
      sourcePdf,
      group.orders.map((order) => order.pageIndex),
    );
    copiedPages.forEach((page) => outputPdf.addPage(page));
  }

  if (plan.complexOrders.length) {
    for (const order of plan.complexOrders) {
      drawComplexOrderDividerPage(outputPdf, pageSize, fonts, order);
      const [copiedPage] = await outputPdf.copyPages(sourcePdf, [order.pageIndex]);
      outputPdf.addPage(copiedPage);
    }
  }

  return outputPdf.save();
}

function downloadPdf(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 2000);
}

export default function OzonFbsLabelSorter() {
  const [labelsFile, setLabelsFile] = useState(null);
  const [assemblyFile, setAssemblyFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [plan, setPlan] = useState(null);

  const summary = useMemo(() => (plan ? formatSummary(plan) : null), [plan]);

  async function handleProcess() {
    if (!labelsFile || !assemblyFile) {
      setError("Нужно загрузить PDF с этикетками и PDF со списком отправлений.");
      setSuccess("");
      return;
    }

    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const [labelBytes, assemblyBytes] = await Promise.all([
        readFileAsUint8Array(labelsFile),
        readFileAsUint8Array(assemblyFile),
      ]);

      const [labelTexts, assemblyTexts] = await Promise.all([
        extractPdfTexts(labelBytes),
        extractPdfTexts(assemblyBytes),
      ]);

      const labelPages = labelTexts.map((text, pageIndex) => ({
        pageIndex,
        orderId: extractLabelPageOrderId(text),
      }));

      const pagesWithoutOrder = labelPages.filter((entry) => !entry.orderId);
      if (pagesWithoutOrder.length) {
        const pageNumbers = pagesWithoutOrder
          .slice(0, 10)
          .map((entry) => entry.pageIndex + 1)
          .join(", ");
        const suffix = pagesWithoutOrder.length > 10 ? " и других" : "";
        throw new Error(
          `Не удалось прочитать номер отправления на страницах: ${pageNumbers}${suffix}.`,
        );
      }

      const records = parseAssemblyRecords(assemblyTexts);
      if (!records.length) {
        throw new Error("Не удалось разобрать список отправлений. Проверьте PDF Ozon.");
      }

      const nextPlan = buildAssemblyPlan(records, labelPages);
      if (nextPlan.unmatchedOrders.length) {
        throw new Error(
          `Не нашлись этикетки для ${nextPlan.unmatchedOrders.length} отправлений.`,
        );
      }

      setPlan({
        ...nextPlan,
        sourceLabelCount: labelPages.length,
      });
      setSuccess("Файлы разобраны. Можно собирать итоговый PDF.");
    } catch (processingError) {
      setPlan(null);
      setError(processingError.message || "Не удалось обработать файлы.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload() {
    if (!plan || !labelsFile) return;

    setBusy(true);
    setError("");

    try {
      const labelBytes = await readFileAsUint8Array(labelsFile);
      const bytes = await buildOutputPdf(labelBytes, plan);
      downloadPdf(bytes, "ozon-fbs-grouped-labels.pdf");
      setSuccess("Итоговый PDF собран.");
    } catch (processingError) {
      setError(processingError.message || "Не удалось собрать итоговый PDF.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ofls">
      <div className="ofls__panel">
        <div className="ofls__intro">
          <span className="ofls__eyebrow">Работа в браузере без регистрации и бесплатно</span>
          <h2>Пересортировка FBS-этикеток Ozon по артикулам</h2>
          <p>
            Загружаете два PDF, а инструмент локально собирает одну ленту:
            сначала разделитель с товаром и артикулом, потом исходные Ozon-этикетки
            этой группы. Сборные заказы уезжают в конец.
          </p>
        </div>

        <div className="ofls__grid">
          <label className="ofls__field">
            <span>1. PDF с этикетками Ozon</span>
            <input
              type="file"
              accept="application/pdf"
              onChange={(event) => setLabelsFile(event.target.files?.[0] || null)}
            />
            <small>Одна этикетка Ozon на страницу, с QR и номером отправления.</small>
          </label>

          <label className="ofls__field">
            <span>2. PDF со списком отправлений</span>
            <input
              type="file"
              accept="application/pdf"
              onChange={(event) => setAssemblyFile(event.target.files?.[0] || null)}
            />
            <small>Оригинальный assembly list из Ozon, где есть товар, артикул и кол-во.</small>
          </label>
        </div>

        <div className="ofls__actions">
          <button type="button" onClick={handleProcess} disabled={busy}>
            {busy ? "Обрабатываем..." : "Разобрать файлы"}
          </button>
          <button
            type="button"
            className="ofls__button-secondary"
            onClick={handleDownload}
            disabled={busy || !plan}
          >
            Скачать итоговый PDF
          </button>
        </div>

        {error ? <div className="ofls__status ofls__status--error">{error}</div> : null}
        {success ? <div className="ofls__status ofls__status--success">{success}</div> : null}
      </div>

      <div className="ofls__panel ofls__panel--muted">
        <h3>Логика группировки</h3>
        <ul className="ofls__list">
          <li>Простой заказ: одна позиция и количество 1.</li>
          <li>Простые заказы группируются по артикулу в порядке первого появления в списке.</li>
          <li>Сборные заказы выводятся в конце отдельным блоком.</li>
          <li>Сами страницы с QR не перерисовываются, меняется только их порядок.</li>
        </ul>
      </div>

      {summary ? (
        <div className="ofls__panel">
          <h3>Что найдено</h3>
          <div className="ofls__stats">
            <div className="ofls__stat">
              <strong>{summary.groupCount}</strong>
              <span>групп по артикулу</span>
            </div>
            <div className="ofls__stat">
              <strong>{summary.simpleLabels}</strong>
              <span>простых этикеток</span>
            </div>
            <div className="ofls__stat">
              <strong>{summary.complexLabels}</strong>
              <span>сборных заказов</span>
            </div>
            <div className="ofls__stat">
              <strong>{plan.sourceLabelCount}</strong>
              <span>исходных страниц</span>
            </div>
          </div>

          <div className="ofls__preview">
            {plan.simpleGroups.slice(0, 8).map((group) => (
              <div key={group.article} className="ofls__preview-card">
                <div className="ofls__preview-article">{group.article}</div>
                <div className="ofls__preview-product">{group.product}</div>
                <div className="ofls__preview-meta">{group.orders.length} этикеток</div>
              </div>
            ))}
          </div>

          {plan.orphanPages.length ? (
            <div className="ofls__status ofls__status--warn">
              Остались страницы этикеток без строки в assembly list: {plan.orphanPages.join(", ")}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
